import { useCallback, useState } from "react";

import { QuoteUnionByMode, ShipperQuoteRequest, useSplitQuoteMutation } from "api/rest/quote-requests";
import useLDFlag from "hooks/useLDFlag";
import { useSnackbar } from "notistack";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";
import { Sentry } from "sentry";

import SplitQuoteDialogView from "./SplitQuoteDialogView";

// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
export const useSplitQuote = (quoteRequest: ShipperQuoteRequest) => {
  const enableMultiloadQuotes = useLDFlag("multiLoadQuotes");
  const shouldDisplaySplitQuote =
    !!enableMultiloadQuotes &&
    !quoteRequest?.original_qr_id &&
    quoteRequest?.status === "RETURNED" &&
    quoteRequest.mode === "FTL";
  const [splitQuoteId, setSplitQuoteId] = useState<number | null>(null);
  const [splitQuote, { isLoading: isLoadingSplitQuote }] = useSplitQuoteMutation();
  const { enqueueSnackbar } = useSnackbar();
  const { t } = useTranslation(["shipper"]);
  const history = useHistory();

  const handleSplitQuote = useCallback(async () => {
    if (!quoteRequest?.id || !splitQuoteId) {
      return;
    }

    try {
      const { data } = await splitQuote({
        urlParams: {
          quoteRequestId: quoteRequest.id,
          quoteId: splitQuoteId,
        },
      }).unwrap();
      enqueueSnackbar(t("shipper:splitQuoteDialog.success"), { variant: "success" });
      history.push(`/shipper/quotes/${data.quote_request_id}/${data.quote_id}`);
    } catch (error) {
      enqueueSnackbar(t("shipper:splitQuoteDialog.error"), { variant: "error" });
      Sentry.captureException(error);
    }
  }, [splitQuote, enqueueSnackbar, history, splitQuoteId, quoteRequest?.id, t]);

  return {
    SplitQuoteDialogView: (
      <SplitQuoteDialogView
        isOpen={!!splitQuoteId}
        onClose={() => setSplitQuoteId(null)}
        onSubmit={handleSplitQuote}
        loading={isLoadingSplitQuote}
        portexId={quoteRequest?.portex_id ?? ""}
        quote={
          !!splitQuoteId
            ? (quoteRequest.quotes.find((q) => q._quote_id === splitQuoteId) as QuoteUnionByMode & {
                mode: "FTL";
              })
            : undefined
        }
      />
    ),
    shouldDisplaySplitQuote,
    setSplitQuoteId,
  };
};
